import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Colors, Fonts, Info } from '@zazume/zzm-base';
import React, { ReactNode } from 'react';
import { Tooltip } from '../tooltip/Tooltip';

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.label`
  flex: 1;
  font-family: ${Fonts.BASE};
  font-style: normal;
  font-weight: normal;
  font-size: 1rem;
  line-height: 2rem;
  color: ${Colors.FONT_3};
`;

const Input = styled.input(({ theme }) => css`
  height: 1.35rem;
  width: 1.35rem;
  border-radius: 0.25rem;
  background: ${theme.colors.White};
  border: ${theme.borderWeights.light} solid ${theme.colors.Gray300};
  box-sizing: border-box;
  box-shadow: 0 0.071rem 0.071rem rgba(0, 0, 0, 0.05);
  outline: none;
  display: inline-block;
  vertical-align: top;
  margin: 0;
  position: relative;
  cursor: pointer;

  @media (min-width: ${theme.breakpoints.sm}) {
    height: 1.5rem;
    width: 1.5rem;
  }

  --active: #275EFE;
  --active-inner: #fff;
  --border: #BBC1E1;
  --border-hover: #275EFE;
  --background: #fff;
  --disabled: #F6F8FF;
  --disabled-inner: #E1E6F9;
  -webkit-appearance: none;
  -moz-appearance: none;
  transition: background .3s, border-color .3s, box-shadow .2s;

  &:checked {
    background: ${theme.colors.Primary};
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    transition: transform .3s cubic-bezier(.2, .85, .32, 1.2), opacity .2s;
    width: 0.357rem;
    height: 0.714rem;
    border: 0.143rem solid var(--active-inner);
    border-top: 0;
    border-left: 0;
    left: 0.350rem;
    top: 0.130rem;
    transform: rotate(var(--r, 35deg));
    -webkit-transform: rotate(35deg);
    -ms-transform: rotate(35deg);

    @media (min-width: ${theme.breakpoints.sm}) {
      left: 0.450rem;
      top: 0.160rem;
    }

    &:checked {
      --r: 35deg;
      display: block;
    }
  }

  &:disabled {
    background: var(--disabled);
    cursor: default;

    &:checked:after {
      display: block;
      border: 0.143rem solid var(--disabled-inner);
      border-top: 0;
      border-left: 0;
    }

    &:not(:checked):after {
      display: none;
    }
  }

  &:focus {
    box-shadow: 0 0 0 0.143rem rgba(39, 94, 254, .3);
  }

  & + label {
    font-size: 1rem;
    line-height: 1.429rem;
    display: inline-block;
    vertical-align: top;
    cursor: pointer;
    margin-left: 0.857rem;
  }
`);

const LabelContent = styled.div<{ disabled: boolean }>(({ theme, disabled }) => css`
  display: flex;
  gap: ${theme.spacing.s2};
  color: ${disabled ? theme.colors.Gray300 : theme.colors.Gray600};
  cursor: ${disabled ? 'default' : 'pointer'};
`);

const TooltipText = styled.div(({ theme }) => css`
  color: ${theme.colors.White};
  text-align: center;
  white-space: pre-wrap;
  letter-spacing: 0;
`);

export interface CheckboxProps extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  name: string;
  label?: any;
  containerStyle?: any;
  tooltip?: string | ReactNode;
}

/**
 * @deprecated use CheckboxBase from zzm-base
 */
export const Checkbox = React.forwardRef<any, CheckboxProps>((props, ref) => {
  const { name, label, containerStyle = {}, tooltip, disabled = false, ...rest } = props;

  const id = props.value ? `${name}_${props.value}` : name;

  return <Container style={containerStyle}>
    <Input ref={ref} id={id} name={name} type="checkbox" disabled={disabled} {...rest}/>
    {label && <Label htmlFor={id}>
      <LabelContent disabled={disabled}>{label}
        {tooltip && <Tooltip title={<Info size={16} color="Gray500"/>}><TooltipText>{tooltip}</TooltipText></Tooltip>}
      </LabelContent>
    </Label>}
  </Container>;
});
